import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import logo from '../../assets/yolo-logo.png';
import { SITE_CONFIG, resolveUrl } from '../siteConfig';
import NavDropdown from './header/NavDropdown';
import ThemeDropdown from './header/ThemeDropdown';
import { productsDropdown, servicesDropdown, simpleNavLinks } from './header/navItems';
import {
  ThemeMode,
  ResolvedTheme,
  THEME_STORAGE_KEY,
  applyTheme,
  getSystemPreference,
  resolveTheme,
} from './header/theme';
import './Header.css';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(false);
  const [themeMode, setThemeMode] = useState<ThemeMode>('system');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');
  const location = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navLinksRef = useRef<HTMLUListElement>(null);
  const wasMobileMenuOpen = useRef(false);
  const { items } = useCart();
  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(max-width: 968px)');
    const update = () => setIsMobileNav(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!isMobileNav) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileNav]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('mobile-menu-open', isMobileMenuOpen);
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      wasMobileMenuOpen.current = true;
      const firstFocusable = navLinksRef.current?.querySelector<HTMLElement>('a, button');
      firstFocusable?.focus();
      return;
    }

    if (wasMobileMenuOpen.current) {
      menuButtonRef.current?.focus();
      wasMobileMenuOpen.current = false;
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const handleTabTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusables: HTMLElement[] = [];
      if (menuButtonRef.current) focusables.push(menuButtonRef.current);
      navLinksRef.current?.querySelectorAll<HTMLElement>('a, button').forEach((el) => {
        if (!el.hasAttribute('disabled')) focusables.push(el);
      });

      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleTabTrap);
    return () => window.removeEventListener('keydown', handleTabTrap);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    } catch {
      stored = null;
    }
    const initialMode: ThemeMode =
      stored === 'light' || stored === 'dark' || stored === 'system'
        ? (stored as ThemeMode)
        : 'system';
    setThemeMode(initialMode);
    const resolved = resolveTheme(initialMode);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  useEffect(() => {
    if (themeMode !== 'system' || !window.matchMedia) return undefined;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = getSystemPreference();
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };

    media.addEventListener?.('change', handleChange);
    return () => media.removeEventListener?.('change', handleChange);
  }, [themeMode]);

  const selectTheme = (mode: ThemeMode) => {
    setThemeMode(mode);
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyTheme(resolved);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Ignore storage access errors.
    }
  };

  const voiceProUrl = resolveUrl(SITE_CONFIG.urls.voicePro);

  const isServicesActive = location.pathname.startsWith('/services');
  const isProductsActive = location.pathname.startsWith('/products');

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="container">
        <nav className="nav" aria-label="Primary">
          <Link to="/" className="logo" aria-label="YOLO Telecoms home">
            <img src={logo || undefined} alt="YOLO Telecoms" className="logo-img" />
          </Link>

          <ul
            className={`nav-links ${isMobileMenuOpen ? 'nav-links-open' : ''}`}
            id="primary-navigation"
            ref={navLinksRef}
            aria-hidden={isMobileNav && !isMobileMenuOpen ? true : undefined}
          >
            <li className="mobile-only mobile-menu-close">
              <button
                type="button"
                className="btn btn-outline btn-sm btn-block"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation"
              >
                Close navigation
              </button>
            </li>
            <NavDropdown label="Services" items={servicesDropdown} isActive={isServicesActive} />
            <NavDropdown label="Products" items={productsDropdown} isActive={isProductsActive} />

            {simpleNavLinks.map((link) => {
              const isActive = link.matchPrefix
                ? location.pathname.startsWith(link.to)
                : location.pathname === link.to;
              return (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`nav-link ${isActive ? 'nav-link-active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}

            <li className="mobile-only">
              <Link
                to="/checkout"
                className={`nav-link cart-nav-link ${location.pathname === '/checkout' ? 'nav-link-active' : ''}`}
                aria-current={location.pathname === '/checkout' ? 'page' : undefined}
              >
                <ShoppingCart size={18} />
                Cart
                {cartItemCount > 0 && (
                  <span className="cart-badge-inline">{cartItemCount}</span>
                )}
              </Link>
            </li>
            <li className="mobile-only mobile-theme-toggle">
              <ThemeDropdown
                currentMode={themeMode}
                resolvedTheme={resolvedTheme}
                onSelect={selectTheme}
                variant="mobile"
              />
            </li>
            <li className="mobile-only mobile-cta">
              <Link to="/contact" className="btn btn-primary btn-block">
                Get a Quote
              </Link>
            </li>
          </ul>

          <div className="nav-actions">
            <a
              href={voiceProUrl}
              target={voiceProUrl !== '#' ? '_blank' : undefined}
              rel={voiceProUrl !== '#' ? 'noopener noreferrer' : undefined}
              className="btn btn-outline btn-sm"
            >
              Portal
            </a>
            <Link to="/contact" className="btn btn-primary btn-sm">
              Get a Quote
            </Link>
            <Link
              to="/checkout"
              className="cart-link"
              aria-label={`Shopping cart with ${cartItemCount} items`}
            >
              <ShoppingCart size={20} />
              {cartItemCount > 0 && (
                <span className="cart-badge">{cartItemCount > 99 ? '99+' : cartItemCount}</span>
              )}
            </Link>
            <ThemeDropdown
              currentMode={themeMode}
              resolvedTheme={resolvedTheme}
              onSelect={selectTheme}
            />
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="primary-navigation"
            ref={menuButtonRef}
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'hamburger-open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </nav>
      </div>
      <div
        className={`mobile-menu-backdrop ${isMobileMenuOpen ? 'is-visible' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />
    </header>
  );
};

export default Header;
